import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '@/config/api';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase,
  UserCheck,
  ClipboardList,
  CheckCircle2,
  Bookmark,
  Target,
  FileText,
  Building2,
  ChevronDown,
  Plus,
  Eye,
  Inbox,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';

import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Skeleton } from '@/Components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/Components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/Components/ui/table';
import { cn } from '@/lib/utils';

import { useRecruiterAuth } from '../../../CompanyAdmin/Context/RecruiterAuthContext';
import RecruiterNavbar from './RecruiterNavbar';
import { CandidateUploadModal } from './CandidateUpload/CandidateUploadModal';
import JobDescriptionModal from './JobDescriptionModal';

// ============================================================================
// Stat cards
// ============================================================================

function HeroStatCard({ value, label, icon: Icon, accent = 'blue' }) {
  const accents = {
    blue: 'bg-blue-50/60 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400',
    violet:
      'bg-violet-50/60 text-violet-600 dark:bg-violet-950/30 dark:text-violet-400',
    emerald:
      'bg-emerald-50/60 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400',
    primary: 'bg-primary/5 text-primary dark:bg-primary/10',
  };

  return (
    <Card className="group border border-border/40 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
      <CardContent className={cn('p-6 rounded-md', accents[accent])}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide font-medium opacity-80">
              {label}
            </p>
            <p className="mt-1 text-3xl font-bold">{value}</p>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-background/70 transition-transform duration-200 group-hover:scale-105">
            <Icon className="h-5 w-5" strokeWidth={2} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PipelineStatCard({ value, label, icon: Icon, color = 'slate' }) {
  const colors = {
    slate: 'bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-300',
    amber:
      'bg-amber-100/70 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    indigo:
      'bg-indigo-100/70 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400',
    teal: 'bg-teal-100/70 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400',
    rose: 'bg-rose-100/70 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400',
    emerald:
      'bg-emerald-100/70 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
  };

  return (
    <Card className="border border-border/30 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
              colors[color],
            )}
          >
            <Icon className="h-4 w-4" strokeWidth={2} />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-xl font-semibold">{value}</span>
            <span className="truncate text-xs text-muted-foreground">{label}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SectionHeader({ title, hint }) {
  return (
    <div className="flex items-center gap-3">
      <h3 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      <div className="h-px flex-1 bg-border/50" />
      {hint && <span className="text-xs text-muted-foreground/70">{hint}</span>}
    </div>
  );
}

// ============================================================================
// Status pill — small color-coded badge for the job table
// ============================================================================

function StatusPill({ status }) {
  const s = (status || '').toLowerCase();
  const variant =
    s === 'open' || s === 'active'
      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
      : s === 'closed' || s === 'inactive'
        ? 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
        : 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400';

  return (
    <Badge
      variant="outline"
      className={cn('border-0 font-medium uppercase text-[10px]', variant)}
    >
      {status || '—'}
    </Badge>
  );
}

// ============================================================================
// Loading skeletons
// ============================================================================

function DashboardSkeleton() {
  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-6">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[110px] w-full rounded-lg" />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-64 w-full rounded-lg" />
    </div>
  );
}

// ============================================================================
// Main page
// ============================================================================

export default function RecruiterDashboard() {
  const { recruiter } = useRecruiterAuth();
  const navigate = useNavigate();

  const [isWeeklyOpen, setIsWeeklyOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDescription, setShowJobDescription] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchDashboard = async () => {
      if (!recruiter?.email) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({ recruiterEmail: recruiter.email });
        const res = await fetch(
          `${API_BASE_URL}/api/recruiter/stats?${params}`,
          { credentials: 'include' },
        );
        if (!res.ok) throw new Error('Failed to fetch dashboard data');
        const data = await res.json();
        if (!cancelled) setDashboardData(data);
      } catch (e) {
        console.error('Dashboard fetch error:', e);
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDashboard();
    return () => {
      cancelled = true;
    };
  }, [recruiter?.email]);

  const handleViewJob = (job) => {
    navigate(`/recruiter/job-details?jobId=${job.job_id}`);
  };

  const handleOpenJD = (e, job) => {
    e.stopPropagation();
    setSelectedJobId(job.job_id);
    setShowJobDescription(true);
  };

  const handleAddCandidate = (e, job) => {
    e.stopPropagation();
    setSelectedJob(job);
    setShowUploadModal(true);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // ---------- loading ----------
  if (loading) {
    return (
      <>
        <RecruiterNavbar />
        <DashboardSkeleton />
      </>
    );
  }

  // ---------- error ----------
  if (error) {
    return (
      <>
        <RecruiterNavbar />
        <div className="mx-auto flex w-full max-w-7xl items-center justify-center px-4 py-16">
          <Card className="w-full max-w-md border-destructive/30">
            <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h2 className="text-lg font-semibold">Unable to load dashboard</h2>
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const { stats = {}, recentJobs = [] } = dashboardData || {};

  return (
    <>
      <RecruiterNavbar />

      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 py-6">
        {/* Greeting */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Welcome back{recruiter?.email ? `, ${recruiter.email.split('@')[0]}` : ''}
          </h1>
          <p className="text-sm text-muted-foreground">
            Here's a snapshot of your hiring pipeline.
          </p>
        </div>

        {/* Hero stats */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <HeroStatCard
            value={stats.openJobs ?? 0}
            label="Open Jobs"
            icon={Briefcase}
            accent="blue"
          />
          <HeroStatCard
            value={stats.totalApplications ?? 0}
            label="Total Applications"
            icon={FileText}
            accent="violet"
          />
          <HeroStatCard
            value={stats.totalCompanies ?? 0}
            label="Total Companies"
            icon={Building2}
            accent="emerald"
          />
          <HeroStatCard
            value={`${stats.hireRate ?? 0}%`}
            label="Hire Rate"
            icon={Target}
            accent="primary"
          />
        </div>

        {/* Pipeline */}
        <div className="flex flex-col gap-3">
          <SectionHeader title="Candidate Pipeline" />
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <PipelineStatCard
              value={stats.totalJoined ?? 0}
              label="Joined"
              icon={UserCheck}
              color="teal"
            />
            <PipelineStatCard
              value={stats.totalHired ?? 0}
              label="Hired"
              icon={CheckCircle2}
              color="emerald"
            />
            <PipelineStatCard
              value={stats.totalShortlisted ?? 0}
              label="Shortlisted"
              icon={Bookmark}
              color="indigo"
            />
            <PipelineStatCard
              value={stats.totalRejected ?? 0}
              label="Rejected"
              icon={ClipboardList}
              color="rose"
            />
          </div>
        </div>

        {/* Weekly */}
        <Collapsible open={isWeeklyOpen} onOpenChange={setIsWeeklyOpen}>
          <CollapsibleTrigger className="group flex w-full items-center gap-3">
            <h3 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              Weekly Performance
            </h3>
            <div className="h-px flex-1 bg-border/50" />
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform duration-200',
                isWeeklyOpen && 'rotate-180',
              )}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-4">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <PipelineStatCard
                value={stats.weeklyApplications ?? 0}
                label="Applications (7d)"
                icon={FileText}
                color="slate"
              />
              <PipelineStatCard
                value={stats.weeklyShortlisted ?? 0}
                label="Shortlisted (7d)"
                icon={Bookmark}
                color="indigo"
              />
              <PipelineStatCard
                value={stats.weeklyHired ?? 0}
                label="Hired (7d)"
                icon={CheckCircle2}
                color="emerald"
              />
              <PipelineStatCard
                value={stats.weeklyJoined ?? 0}
                label="Joined (7d)"
                icon={UserCheck}
                color="teal"
              />
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Recently posted jobs */}
        <Card className="border border-border/40 shadow-sm">
          <div className="flex items-center justify-between border-b border-border/40 px-6 py-4">
            <div>
              <h3 className="text-base font-semibold">Recently Posted Jobs</h3>
              <p className="text-xs text-muted-foreground">
                Your 5 most recently opened roles
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/recruiter/my-jobs')}
            >
              View all
            </Button>
          </div>

          {recentJobs.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <Inbox className="h-10 w-10 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">No recent jobs</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/40">
                    <TableHead className="w-[28%]">Position</TableHead>
                    <TableHead className="text-right">Vacancies</TableHead>
                    <TableHead className="text-right">Applications</TableHead>
                    <TableHead className="text-right">Shortlisted</TableHead>
                    <TableHead className="text-right">Hired</TableHead>
                    <TableHead className="text-right">Joined</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentJobs.map((job) => (
                    <TableRow
                      key={job.job_id}
                      className="cursor-pointer border-border/40 hover:bg-muted/40"
                      onClick={() => handleViewJob(job)}
                    >
                      <TableCell className="py-3">
                        <div className="font-medium">{job.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {job.location} · Opened {formatDate(job.posted_on)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {job.total_vacancies ?? 1}
                      </TableCell>
                      <ClickableNumberCell
                        value={job.applications}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/recruiter/job-details?jobId=${job.job_id}`,
                            { state: { filter: 'all' } },
                          );
                        }}
                      />
                      <ClickableNumberCell
                        value={job.shortlisted}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/recruiter/job-details?jobId=${job.job_id}`,
                            { state: { filter: 'shortlisted' } },
                          );
                        }}
                      />
                      <ClickableNumberCell
                        value={job.hired}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/recruiter/job-details?jobId=${job.job_id}`,
                            { state: { filter: 'hired' } },
                          );
                        }}
                      />
                      <ClickableNumberCell
                        value={job.joined ?? 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/recruiter/job-details?jobId=${job.job_id}`,
                            { state: { filter: 'joined' } },
                          );
                        }}
                      />
                      <TableCell>
                        <StatusPill status={job.status} />
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => handleOpenJD(e, job)}
                          >
                            <Eye className="mr-1 h-3.5 w-3.5" />
                            JD
                          </Button>
                          <Button
                            size="sm"
                            variant="default"
                            onClick={(e) => handleAddCandidate(e, job)}
                          >
                            <Plus className="mr-1 h-3.5 w-3.5" />
                            Add
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* Modals */}
      <CandidateUploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        jobId={selectedJob?.job_id}
        jobTitle={selectedJob?.title}
        recruiterEmail={recruiter?.email}
        onSuccess={() => window.dispatchEvent(new Event('refreshJobs'))}
      />

      {selectedJobId && showJobDescription && (
        <JobDescriptionModal
          jobId={selectedJobId}
          recruiterEmail={recruiter?.email}
          onClose={() => {
            setSelectedJobId(null);
            setShowJobDescription(false);
          }}
        />
      )}
    </>
  );
}

function ClickableNumberCell({ value, onClick }) {
  return (
    <TableCell
      className="cursor-pointer text-right font-medium tabular-nums hover:text-primary"
      onClick={onClick}
    >
      {value}
    </TableCell>
  );
}
